import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { adminAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TrendingUp, ArrowLeft, Check, X, Clock, DollarSign, Users, FileText, Eye, ExternalLink, Activity, 
  Pencil, Trash2, Search, Key
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('deposits');
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [pendingKYC, setPendingKYC] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [viewingProof, setViewingProof] = useState(null);
  const [marketStatus, setMarketStatus] = useState(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ master: '', investor: '' });
  const [editingFunds, setEditingFunds] = useState(null);
  const [fundsForm, setFundsForm] = useState({ balance: 0, demo_balance: 0, credit: 0 });

  useEffect(() => {
    fetchData();
    fetchMarketStatus();
  }, []);

  const fetchMarketStatus = async () => {
    try {
      const res = await adminAPI.getMarketStatus();
      setMarketStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch market status:', error);
    }
  };

  const toggleMarketStatus = async () => {
    setMarketLoading(true);
    try {
      const newStatus = !marketStatus?.admin_override?.is_open;
      await adminAPI.setMarketStatus(newStatus, true);
      toast.success(`Market ${newStatus ? 'opened' : 'closed'} successfully`);
      fetchMarketStatus();
    } catch (error) {
      toast.error('Failed to update market status');
    } finally {
      setMarketLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [depositsRes, withdrawalsRes, usersRes, kycRes] = await Promise.all([
        adminAPI.getPendingDeposits(),
        adminAPI.getPendingWithdrawals(),
        adminAPI.getAllUsers(),
        adminAPI.getPendingKYC(),
      ]);
      setPendingDeposits(depositsRes.data);
      setPendingWithdrawals(withdrawalsRes.data);
      setAllUsers(usersRes.data);
      setPendingKYC(kycRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveDeposit = async (transactionId) => {
    setProcessing(transactionId);
    try {
      await adminAPI.approveDeposit(transactionId);
      toast.success('Deposit approved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve deposit');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectDeposit = async (transactionId) => {
    setProcessing(transactionId);
    try {
      await adminAPI.rejectDeposit(transactionId);
      toast.success('Deposit rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject deposit');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveWithdrawal = async (transactionId) => {
    setProcessing(transactionId);
    try {
      await adminAPI.approveWithdrawal(transactionId);
      toast.success('Withdrawal approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve withdrawal');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectWithdrawal = async (transactionId) => {
    setProcessing(transactionId);
    try {
      await adminAPI.rejectWithdrawal(transactionId);
      toast.success('Withdrawal rejected and refunded');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject withdrawal');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveKYC = async (userId) => {
    setProcessing(userId);
    try {
      await adminAPI.approveKYC(userId);
      toast.success('KYC approved successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve KYC');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectKYC = async (userId) => {
    setProcessing(userId);
    try {
      await adminAPI.rejectKYC(userId);
      toast.success('KYC rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject KYC');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setProcessing(userId);
    try {
      await adminAPI.deleteUser(userId);
      toast.success('User deleted successfully');
      setDeletingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setProcessing(null);
    }
  };

  const handleModifyUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setProcessing(editingUser.id);
    try {
      const updateData = {
        user_id: editingUser.id,
        email: editingUser.email,
        first_name: editingUser.first_name,
        last_name: editingUser.last_name,
        phone: editingUser.phone,
        account_type: editingUser.account_type,
        kyc_status: editingUser.kyc_status,
        is_admin: editingUser.is_admin,
        balance: parseFloat(editingUser.balance) || 0,
        demo_balance: parseFloat(editingUser.demo_balance) || 0,
        credit: parseFloat(editingUser.credit) || 0,
      };
      
      await adminAPI.modifyUser(updateData);
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setProcessing(null);
    }
  };

  const handleEditFunds = async (e) => {
    e.preventDefault();
    if (!editingFunds) return;
    
    setProcessing(editingFunds.id);
    try {
      const updateData = {
        user_id: editingFunds.id,
        balance: parseFloat(fundsForm.balance) || 0,
        demo_balance: parseFloat(fundsForm.demo_balance) || 0,
        credit: parseFloat(fundsForm.credit) || 0,
      };
      
      await adminAPI.modifyUser(updateData);
      toast.success('Funds updated successfully');
      setEditingFunds(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update funds');
    } finally {
      setProcessing(null);
    }
  };

  const openEditFunds = (user) => {
    setEditingFunds(user);
    setFundsForm({
      balance: user.balance || 0,
      demo_balance: user.demo_balance || 0,
      credit: user.credit || 0,
    });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPasswordUser) return;
    
    if (passwordForm.master.length < 6 || passwordForm.investor.length < 6) {
      toast.error('Passwords must be at least 6 characters');
      return;
    }
    if (passwordForm.master === passwordForm.investor) {
      toast.error('Master and Investor passwords must be different');
      return;
    }
    
    setProcessing(resetPasswordUser.id);
    try {
      await adminAPI.resetUserPassword(resetPasswordUser.id, passwordForm.master, passwordForm.investor);
      toast.success('Password reset successfully');
      setResetPasswordUser(null);
      setPasswordForm({ master: '', investor: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setProcessing(null);
    }
  };

  const filteredUsers = allUsers.filter(u => {
    if (!userSearch) return true;
    const search = userSearch.toLowerCase();
    return (
      u.email?.toLowerCase().includes(search) ||
      u.first_name?.toLowerCase().includes(search) ||
      u.last_name?.toLowerCase().includes(search) ||
      u.id?.toLowerCase().includes(search)
    );
  });

  const getProofUrl = (proofPath) => {
    if (!proofPath) return null;
    // Handle both full URLs and relative paths
    if (proofPath.startsWith('http')) return proofPath;
    return `${BACKEND_URL}${proofPath}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <header className="h-14 border-b border-zinc-800 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white">Admin Panel</span>
          </div>
        </div>
        <span className="text-zinc-400 text-sm">{user?.email}</span>
      </header>

      {/* Market Control */}
      <div className="p-4">
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className={`w-6 h-6 ${marketStatus?.current_status?.is_open ? 'text-green-400' : 'text-red-400'}`} />
              <div>
                <h3 className="text-white font-semibold">Market Status Control</h3>
                <p className="text-sm text-zinc-400">
                  {marketStatus?.current_status?.is_open ? 'Market is OPEN' : 'Market is CLOSED'} 
                  {marketStatus?.admin_override?.enabled && ' (Admin Override)'}
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  Note: Crypto trading is always available regardless of market status
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${marketStatus?.admin_override?.is_open ? 'text-green-400' : 'text-red-400'}`}>
                {marketStatus?.admin_override?.is_open ? 'OPEN' : 'CLOSED'}
              </span>
              <Switch
                checked={marketStatus?.admin_override?.is_open ?? true}
                onCheckedChange={toggleMarketStatus}
                disabled={marketLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 p-4">
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 text-yellow-400 mb-2">
            <Clock className="w-5 h-5" />
            <span className="text-sm">Pending Deposits</span>
          </div>
          <div className="text-2xl font-bold text-white">{pendingDeposits.length}</div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 text-orange-400 mb-2">
            <DollarSign className="w-5 h-5" />
            <span className="text-sm">Pending Withdrawals</span>
          </div>
          <div className="text-2xl font-bold text-white">{pendingWithdrawals.length}</div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 text-purple-400 mb-2">
            <FileText className="w-5 h-5" />
            <span className="text-sm">Pending KYC</span>
          </div>
          <div className="text-2xl font-bold text-white">{pendingKYC.length}</div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 text-blue-400 mb-2">
            <Users className="w-5 h-5" />
            <span className="text-sm">Total Users</span>
          </div>
          <div className="text-2xl font-bold text-white">{allUsers.length}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800 px-4">
        {['deposits', 'withdrawals', 'kyc', 'users'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-3 text-sm font-medium uppercase transition-colors ${
              activeTab === tab
                ? 'text-white border-b-2 border-primary'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'deposits' && (
          <div className="glass-card">
            <div className="p-4 border-b border-zinc-800">
              <h2 className="text-white font-semibold">Pending Deposit Requests</h2>
            </div>
            <ScrollArea className="h-[500px]">
              {pendingDeposits.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">No pending deposits</div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {pendingDeposits.map((deposit) => (
                    <div key={deposit.id} className="p-4 hover:bg-zinc-800/30">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="text-white font-medium">{deposit.user_email}</div>
                          <div className="text-zinc-500 text-sm">ID: {deposit.id.slice(0, 8)}...</div>
                        </div>
                        <div className="text-right">
                          <div className="text-green-400 font-mono font-bold">
                            +{formatCurrency(deposit.amount)}
                          </div>
                          <div className="text-zinc-500 text-xs">{deposit.currency} ({deposit.network})</div>
                        </div>
                      </div>
                      <div className="text-zinc-400 text-sm mb-2">
                        TX Hash: {deposit.tx_hash || 'Not provided'}
                      </div>
                      {/* Deposit Proof Section */}
                      {deposit.proof_url && (
                        <div className="mb-3 p-2 bg-zinc-900 rounded border border-zinc-700">
                          <div className="flex items-center justify-between">
                            <span className="text-zinc-400 text-sm flex items-center gap-1">
                              <FileText className="w-4 h-4" /> Deposit Proof
                            </span>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-zinc-600"
                                onClick={() => setViewingProof(viewingProof === deposit.id ? null : deposit.id)}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                {viewingProof === deposit.id ? 'Hide' : 'View'}
                              </Button>
                              <a
                                href={getProofUrl(deposit.proof_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center h-7 px-2 text-xs border border-zinc-600 rounded hover:bg-zinc-800 text-zinc-300"
                              >
                                <ExternalLink className="w-3 h-3 mr-1" /> Open
                              </a>
                            </div>
                          </div>
                          {viewingProof === deposit.id && (
                            <div className="mt-2">
                              <img 
                                src={getProofUrl(deposit.proof_url)} 
                                alt="Deposit Proof" 
                                className="max-w-full max-h-48 rounded border border-zinc-700 object-contain"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <div className="hidden text-zinc-500 text-xs mt-1">
                                Unable to preview. Use Open to view in new tab.
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="text-zinc-500 text-xs mb-3">
                        {formatDate(deposit.created_at)}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApproveDeposit(deposit.id)}
                          disabled={processing === deposit.id}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleRejectDeposit(deposit.id)}
                          disabled={processing === deposit.id}
                          variant="outline"
                          className="flex-1 border-red-500 text-red-400 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        )}

        {activeTab === 'withdrawals' && (
          <div className="glass-card">
            <div className="p-4 border-b border-zinc-800">
              <h2 className="text-white font-semibold">Pending Withdrawal Requests</h2>
            </div>
            <ScrollArea className="h-[500px]">
              {pendingWithdrawals.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">No pending withdrawals</div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {pendingWithdrawals.map((withdrawal) => (
                    <div key={withdrawal.id} className="p-4 hover:bg-zinc-800/30">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="text-white font-medium">{withdrawal.user_email}</div>
                          <div className="text-zinc-500 text-sm">ID: {withdrawal.id.slice(0, 8)}...</div>
                        </div>
                        <div className="text-right">
                          <div className="text-red-400 font-mono font-bold">
                            -{formatCurrency(withdrawal.amount)}
                          </div>
                          <div className="text-zinc-500 text-xs">{withdrawal.currency} ({withdrawal.network})</div>
                        </div>
                      </div>
                      <div className="text-zinc-400 text-sm mb-2">
                        To: {withdrawal.wallet_address}
                      </div>
                      <div className="text-zinc-500 text-xs mb-3">
                        {formatDate(withdrawal.created_at)}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApproveWithdrawal(withdrawal.id)}
                          disabled={processing === withdrawal.id}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleRejectWithdrawal(withdrawal.id)}
                          disabled={processing === withdrawal.id}
                          variant="outline"
                          className="flex-1 border-red-500 text-red-400 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject & Refund
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        )}

        {activeTab === 'kyc' && (
          <div className="glass-card">
            <div className="p-4 border-b border-zinc-800">
              <h2 className="text-white font-semibold">Pending KYC Verification Requests</h2>
            </div>
            <ScrollArea className="h-[500px]">
              {pendingKYC.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">No pending KYC requests</div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {pendingKYC.map((kycUser) => (
                    <div key={kycUser.id} className="p-4 hover:bg-zinc-800/30">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="text-white font-medium">{kycUser.first_name} {kycUser.last_name}</div>
                          <div className="text-zinc-500 text-sm">{kycUser.email}</div>
                          <div className="text-zinc-600 text-xs">Account ID: {kycUser.account_id}</div>
                        </div>
                        <div className="text-right">
                          <span className="px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400">
                            Pending Review
                          </span>
                        </div>
                      </div>
                      
                      {/* KYC Documents */}
                      {kycUser.kyc_documents && Object.keys(kycUser.kyc_documents).length > 0 && (
                        <div className="mb-3 space-y-2">
                          <div className="text-zinc-400 text-sm font-medium">Uploaded Documents:</div>
                          {Object.entries(kycUser.kyc_documents).map(([docType, docInfo]) => (
                            <div key={docType} className="p-2 bg-zinc-900 rounded border border-zinc-700">
                              <div className="flex items-center justify-between">
                                <span className="text-zinc-300 text-sm capitalize flex items-center gap-1">
                                  <FileText className="w-4 h-4" /> {docType.replace('_', ' ')}
                                </span>
                                <div className="flex gap-2">
                                  {docInfo.url && (
                                    <a
                                      href={getProofUrl(docInfo.url)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center h-7 px-2 text-xs border border-zinc-600 rounded hover:bg-zinc-800 text-zinc-300"
                                    >
                                      <ExternalLink className="w-3 h-3 mr-1" /> View Document
                                    </a>
                                  )}
                                </div>
                              </div>
                              <div className="text-zinc-500 text-xs mt-1">
                                Uploaded: {formatDate(docInfo.uploaded_at)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="text-zinc-500 text-xs mb-3">
                        Submitted: {formatDate(kycUser.updated_at || kycUser.created_at)}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApproveKYC(kycUser.id)}
                          disabled={processing === kycUser.id}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve KYC
                        </Button>
                        <Button
                          onClick={() => handleRejectKYC(kycUser.id)}
                          disabled={processing === kycUser.id}
                          variant="outline"
                          className="flex-1 border-red-500 text-red-400 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="glass-card">
            <div className="p-4 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <h2 className="text-white font-semibold">All Users ({filteredUsers.length})</h2>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-9 bg-zinc-900 border-zinc-700"
                  />
                </div>
              </div>
            </div>
            <ScrollArea className="h-[500px]">
              <div className="divide-y divide-zinc-800">
                {filteredUsers.map((u) => (
                  <div key={u.id} className="p-4 hover:bg-zinc-800/30">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{u.first_name} {u.last_name}</span>
                          {u.is_admin && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">Admin</span>
                          )}
                        </div>
                        <div className="text-zinc-500 text-sm">{u.email}</div>
                        <div className="text-zinc-600 text-xs font-mono">ID: {u.id}</div>
                        <div className="flex gap-3 mt-1 text-xs">
                          <span className={`px-1.5 py-0.5 rounded ${
                            u.account_type === 'live' ? 'bg-green-500/20 text-green-400' :
                            u.account_type === 'demo' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-zinc-700 text-zinc-400'
                          }`}>
                            {u.account_type?.toUpperCase() || 'DEMO'}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded ${
                            u.kyc_status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            u.kyc_status === 'submitted' ? 'bg-yellow-500/20 text-yellow-400' :
                            u.kyc_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-zinc-700 text-zinc-400'
                          }`}>
                            KYC: {u.kyc_status || 'pending'}
                          </span>
                        </div>
                      </div>
                      <div className="text-right mr-4">
                        <div className="text-white font-mono">{formatCurrency(u.balance || 0)}</div>
                        <div className="text-zinc-500 text-xs">Live Balance</div>
                        <div className="text-zinc-400 font-mono text-sm">{formatCurrency(u.demo_balance || 0)}</div>
                        <div className="text-zinc-600 text-xs">Demo Balance</div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditFunds(u)}
                          className="h-8 text-xs border-green-900 text-green-400 hover:bg-green-500/10"
                        >
                          <DollarSign className="w-3 h-3 mr-1" /> Funds
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingUser({...u})}
                          className="h-8 text-xs border-zinc-700"
                        >
                          <Pencil className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setResetPasswordUser(u)}
                          className="h-8 text-xs border-zinc-700"
                        >
                          <Key className="w-3 h-3 mr-1" /> Password
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeletingUser(u)}
                          disabled={u.is_admin}
                          className="h-8 text-xs border-red-900 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditingUser(null)}>
          <div className="bg-[#0f0f11] border border-zinc-800 rounded-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Edit User</h2>
              <button onClick={() => setEditingUser(null)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleModifyUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">First Name</Label>
                  <Input
                    value={editingUser.first_name || ''}
                    onChange={(e) => setEditingUser({...editingUser, first_name: e.target.value})}
                    className="bg-zinc-900 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Last Name</Label>
                  <Input
                    value={editingUser.last_name || ''}
                    onChange={(e) => setEditingUser({...editingUser, last_name: e.target.value})}
                    className="bg-zinc-900 border-zinc-700"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Email</Label>
                <Input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  className="bg-zinc-900 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Phone</Label>
                <Input
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                  className="bg-zinc-900 border-zinc-700"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Account Type</Label>
                  <Select value={editingUser.account_type || 'demo'} onValueChange={(v) => setEditingUser({...editingUser, account_type: v})}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="demo">Demo</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="ecn">ECN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">KYC Status</Label>
                  <Select value={editingUser.kyc_status || 'pending'} onValueChange={(v) => setEditingUser({...editingUser, kyc_status: v})}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Live Balance ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingUser.balance || 0}
                    onChange={(e) => setEditingUser({...editingUser, balance: e.target.value})}
                    className="bg-zinc-900 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Demo Balance ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingUser.demo_balance || 0}
                    onChange={(e) => setEditingUser({...editingUser, demo_balance: e.target.value})}
                    className="bg-zinc-900 border-zinc-700"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Credit ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editingUser.credit || 0}
                  onChange={(e) => setEditingUser({...editingUser, credit: e.target.value})}
                  className="bg-zinc-900 border-zinc-700"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                <span className="text-zinc-300">Admin Privileges</span>
                <Switch
                  checked={editingUser.is_admin || false}
                  onCheckedChange={(v) => setEditingUser({...editingUser, is_admin: v})}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditingUser(null)} className="flex-1 border-zinc-700">
                  Cancel
                </Button>
                <Button type="submit" disabled={processing === editingUser.id} className="flex-1">
                  {processing === editingUser.id ? <span className="spinner mr-2" /> : null}
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setResetPasswordUser(null)}>
          <div className="bg-[#0f0f11] border border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Reset Password</h2>
              <button onClick={() => setResetPasswordUser(null)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-zinc-400 text-sm mb-4">
              Reset password for: <span className="text-white">{resetPasswordUser.email}</span>
            </p>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label className="text-zinc-300">New Master Password</Label>
                <Input
                  type="password"
                  value={passwordForm.master}
                  onChange={(e) => setPasswordForm({...passwordForm, master: e.target.value})}
                  placeholder="Min 6 characters"
                  className="bg-zinc-900 border-zinc-700"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-300">New Investor Password</Label>
                <Input
                  type="password"
                  value={passwordForm.investor}
                  onChange={(e) => setPasswordForm({...passwordForm, investor: e.target.value})}
                  placeholder="Min 6 characters"
                  className="bg-zinc-900 border-zinc-700"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setResetPasswordUser(null)} className="flex-1 border-zinc-700">
                  Cancel
                </Button>
                <Button type="submit" disabled={processing === resetPasswordUser.id} className="flex-1">
                  {processing === resetPasswordUser.id ? <span className="spinner mr-2" /> : null}
                  Reset Password
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDeletingUser(null)}>
          <div className="bg-[#0f0f11] border border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-red-400">Delete User</h2>
              <button onClick={() => setDeletingUser(null)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-white mb-2">Are you sure you want to delete this user?</p>
              <p className="text-zinc-400 text-sm">
                <strong>Email:</strong> {deletingUser.email}<br />
                <strong>ID:</strong> {deletingUser.id}
              </p>
              <p className="text-red-400 text-sm mt-3">
                ⚠️ This will permanently delete the user and all their trades and transactions.
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeletingUser(null)} className="flex-1 border-zinc-700">
                Cancel
              </Button>
              <Button 
                onClick={() => handleDeleteUser(deletingUser.id)} 
                disabled={processing === deletingUser.id}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {processing === deletingUser.id ? <span className="spinner mr-2" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Delete User
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Funds Modal */}
      {editingFunds && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditingFunds(null)}>
          <div className="bg-[#0f0f11] border border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-green-400 flex items-center gap-2">
                <DollarSign className="w-5 h-5" /> Edit Funds
              </h2>
              <button onClick={() => setEditingFunds(null)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4 p-3 bg-zinc-900 rounded-lg">
              <p className="text-white font-medium">{editingFunds.first_name} {editingFunds.last_name}</p>
              <p className="text-zinc-400 text-sm">{editingFunds.email}</p>
              <p className="text-zinc-500 text-xs font-mono">ID: {editingFunds.id}</p>
            </div>
            <form onSubmit={handleEditFunds} className="space-y-4">
              <div>
                <Label className="text-zinc-300">Live Balance ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={fundsForm.balance}
                  onChange={(e) => setFundsForm({...fundsForm, balance: e.target.value})}
                  className="bg-zinc-900 border-zinc-700 text-lg font-mono"
                />
                <p className="text-zinc-500 text-xs mt-1">Current: {formatCurrency(editingFunds.balance || 0)}</p>
              </div>
              <div>
                <Label className="text-zinc-300">Demo Balance ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={fundsForm.demo_balance}
                  onChange={(e) => setFundsForm({...fundsForm, demo_balance: e.target.value})}
                  className="bg-zinc-900 border-zinc-700 text-lg font-mono"
                />
                <p className="text-zinc-500 text-xs mt-1">Current: {formatCurrency(editingFunds.demo_balance || 0)}</p>
              </div>
              <div>
                <Label className="text-zinc-300">Credit ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={fundsForm.credit}
                  onChange={(e) => setFundsForm({...fundsForm, credit: e.target.value})}
                  className="bg-zinc-900 border-zinc-700 text-lg font-mono"
                />
                <p className="text-zinc-500 text-xs mt-1">Current: {formatCurrency(editingFunds.credit || 0)}</p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditingFunds(null)} className="flex-1 border-zinc-700">
                  Cancel
                </Button>
                <Button type="submit" disabled={processing === editingFunds.id} className="flex-1 bg-green-600 hover:bg-green-700">
                  {processing === editingFunds.id ? <span className="spinner mr-2" /> : <DollarSign className="w-4 h-4 mr-1" />}
                  Update Funds
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
